import React, { useState, useEffect, useRef } from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import { space } from 'style/config';
import {
    Box, Flex, Text, Icon, Divider
} from 'components/ui';
import anaUtils from 'analytics/utils';
import anaConsts from 'analytics/constants';
import * as pdpAnalytics from 'analytics/productPage/pdpAnalytics';
import { DebouncedResize } from 'constants/events';
import helpersUtils from 'utils/Helpers';

const { deferTaskExecution } = helpersUtils;

const Accordion = ({ title, id, dataAt, children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [height, setHeight] = useState(0);
    const [width, setWidth] = useState(0);
    const heightRef = useRef(null);
    const widthRef = useRef(null);

    const handleResize = () => {
        setHeight(heightRef.current?.scrollHeight);
        setWidth(widthRef.current?.offsetWidth);
    };

    const openAnchorContent = () => {
        const hash = window.location.hash;

        if (hash !== '' && hash.indexOf(id) > -1) {
            setIsOpen(true);
        }
    };

    useEffect(() => {
        handleResize();
        openAnchorContent();
        window.addEventListener('hashchange', openAnchorContent);
        window.addEventListener(DebouncedResize, handleResize);

        return () => {
            window.removeEventListener(DebouncedResize, handleResize);
            window.removeEventListener('hashchange', openAnchorContent);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        handleResize();
    });

    const handleClick = () => {
        setIsOpen(!isOpen);
        deferTaskExecution(() => {
            const currentEventData = anaUtils.getLastAsyncPageLoadData();
            pdpAnalytics.trackLinkClick({
                linkName: 'D=c55',
                actionInfo: `product:${title.toLowerCase()}`,
                eventStrings: [anaConsts.Event.EVENT_71],
                ...currentEventData
            });
        });
    };

    return (
        <React.Fragment>
            <Divider />
            <Flex
                is='button'
                type='button'
                data-at={dataAt}
                ref={widthRef}
                onClick={handleClick}
                width='100%'
                alignItems='center'
                aria-controls={id}
                aria-expanded={isOpen}
                css={{
                    background: 'transparent',
                    border: 0,
                    padding: 0,
                    textAlign: 'left',
                    cursor: 'pointer',
                    outline: 0,
                    ':focus .Accordion-icon': {
                        outline: '1px dashed',
                        outlineOffset: space[1]
                    }
                }}
            >
                <Text
                    id={`${id}_heading`}
                    is='h2'
                    lineHeight='tight'
                    paddingY='1em'
                    fontSize={['md', 'lg']}
                    fontWeight='bold'
                    children={title}
                />
                <Icon
                    className='Accordion-icon'
                    name={isOpen ? 'caretUp' : 'caretDown'}
                    size={24}
                    marginLeft={width > 650 ? 1 : 'auto'}
                />
            </Flex>

            <div
                id={id}
                aria-labelledby={`${id}_heading`}
                css={{
                    transition: 'height .3s',
                    overflow: 'hidden'
                }}
                style={{
                    height: isOpen ? height : 0
                }}
            >
                <Box
                    ref={heightRef}
                    paddingBottom={[5, 7]}
                    marginLeft={[null, null, 212]}
                    lineHeight={[null, 'relaxed']}
                    maxWidth={824}
                    children={children}
                />
            </div>
        </React.Fragment>
    );
};

export default wrapFunctionalComponent(Accordion, 'Accordion');
